import { useCallback, useMemo } from 'react';
import { useDataFeedWizardStore } from '../../../store/dataFeedWizard';
import { DataFeedRowData } from '../../../types/generatedTypes';

/**
 * Shared hook to determine which rows should be disabled based on account upload settings.
 * A row is disabled if its account number has uploadTransactionsEnabled === false in the mappings.
 */
export const useDisabledRows = () => {
  const { configureMappingData, linkAccountsData } = useDataFeedWizardStore();

  // Compute set of disabled account numbers from Step 3
  const disabledAccountNumbers = useMemo(() => {
    return new Set(
      linkAccountsData.mappings
        .filter(m => m.uploadTransactionsEnabled === false)
        .map(m => m.accountNumber)
    );
  }, [linkAccountsData.mappings]);

  // Helper to check if a row is disabled based on account upload settings
  const isRowDisabled = useCallback(
    (row: DataFeedRowData) => {
      if (!configureMappingData.accountNumberColumn) return false;
      const accountNumber = row[configureMappingData.accountNumberColumn];
      let isDisabled = (
        accountNumber !== undefined &&
        accountNumber !== null &&
        disabledAccountNumbers.has(String(accountNumber))
      );
      return isDisabled;
    },
    [configureMappingData.accountNumberColumn, disabledAccountNumbers]
  );

  // Check if an item appears ONLY in disabled accounts
  const isItemDisabledAccountOnly = useCallback(
    (accountNumbers: string[] | null | undefined): boolean => {
      if (!accountNumbers || accountNumbers.length === 0) return false;
      return accountNumbers.every(acc => disabledAccountNumbers.has(acc));
    },
    [disabledAccountNumbers]
  );

  return { isRowDisabled, disabledAccountNumbers, isItemDisabledAccountOnly };
};
